import { useRef, useState } from "react";
import Bugsnag from "@bugsnag/js";
import { format } from "date-fns";
import { AlertTriangle, FileText, Loader2, Replace, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useOrganizationContext } from "@/hooks/use-organization-context";
import { storageService } from "@/services/storageService";
import type { OrganizationContextDocument, OrganizationContextScope } from "@/services/organizationContextService";
import { OrganizationContextNotificationBell } from "./OrganizationContextNotificationBell";

const ACCEPT = ".pdf,.doc,.docx,.txt,.md";
const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPTED_MIME_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
  "text/markdown",
];

export interface InstitutionalContextDocumentsCardProps {
  variant: OrganizationContextScope;
  scopeKey: string;
  entityLabel: string;
  canManage: boolean;
}

function reportSafeFailure(action: string, error: unknown): void {
  Bugsnag.notify(error instanceof Error ? error : new Error("organization context request failed"), (event) => {
    event.severity = "warning";
    event.addMetadata("organization_context", { action });
  });
}

export const InstitutionalContextDocumentsCard = ({
  variant,
  scopeKey,
  entityLabel,
  canManage,
}: InstitutionalContextDocumentsCardProps) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [selectedFileId, setSelectedFileId] = useState<string>("");
  const [staffSourceVisible, setStaffSourceVisible] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<OrganizationContextDocument | null>(null);
  const context = useOrganizationContext(variant, variant === "school" ? scopeKey : null, canManage);
  const documents = context.documents.data ?? [];
  const libraryFiles = context.libraryFiles.data ?? [];
  const isMutating = isUploading || context.create.isPending || context.replace.isPending || context.remove.isPending || context.updateInclusion.isPending;

  const showFailure = (action: string, error: unknown) => {
    reportSafeFailure(action, error);
  };

  const createFromFile = async (canonicalFileId: string) => {
    if (!canManage) return;
    try {
      await context.create.mutateAsync({
        requestId: crypto.randomUUID(),
        canonicalFileId,
        scope: variant,
        schoolId: variant === "school" ? scopeKey : null,
        requestedInclusion: true,
        staffSourceVisible,
      });
      setSelectedFileId("");
    } catch (error) {
      showFailure("create", error);
    }
  };

  const uploadAndCreate = async (file: File) => {
    if (!canManage || file.size > MAX_BYTES) return;
    setIsUploading(true);
    try {
      const uploaded = await storageService.uploadFile(
        file,
        "organization_context",
        undefined,
        undefined,
        { allowedTypes: ACCEPTED_MIME_TYPES, maxSizeBytes: MAX_BYTES },
      );
      await createFromFile(uploaded.id);
    } catch (error) {
      showFailure("upload_and_create", error);
    } finally {
      setIsUploading(false);
    }
  };

  const changeInclusion = async (document: OrganizationContextDocument, requestedInclusion: boolean) => {
    try {
      await context.updateInclusion.mutateAsync({
        contextDocumentId: document.id,
        requestedInclusion,
        staffSourceVisible: document.staffSourceVisible,
      });
    } catch (error) {
      showFailure("update_inclusion", error);
    }
  };

  const changeSourceVisibility = async (document: OrganizationContextDocument, checked: boolean) => {
    try {
      await context.updateInclusion.mutateAsync({
        contextDocumentId: document.id,
        requestedInclusion: document.requestedInclusion,
        staffSourceVisible: checked,
      });
    } catch (error) {
      showFailure("update_source_visibility", error);
    }
  };

  const createReplacement = async (document: OrganizationContextDocument, canonicalFileId: string) => {
    try {
      await context.replace.mutateAsync({
        requestId: crypto.randomUUID(),
        contextDocumentId: document.id,
        canonicalFileId,
      });
    } catch (error) {
      showFailure("replace", error);
    }
  };

  const removeDocument = async () => {
    if (!pendingDelete) return;
    try {
      await context.remove.mutateAsync({ requestId: crypto.randomUUID(), contextDocumentId: pendingDelete.id });
      setPendingDelete(null);
    } catch (error) {
      showFailure("delete", error);
    }
  };

  return (
    <section className="rounded-2xl border border-stone-200 bg-stone-100/90 p-5 shadow-sm md:p-6" aria-labelledby="organization-context-heading">
      <header className="space-y-3 border-b border-stone-200/80 pb-4 md:pb-5">
        <div>
          <div className="flex items-center justify-between gap-3">
            <h3 id="organization-context-heading" className="font-literata text-base font-semibold text-stone-900 md:text-lg">
              Organization context documents
            </h3>
            <OrganizationContextNotificationBell />
          </div>
          <p className="mt-2 max-w-3xl font-inter text-xs leading-relaxed text-stone-600 md:text-sm">
            These canonical library files can inform chat only after privacy review. Requested inclusion and effective chat use are separate; staff source access is controlled independently.
          </p>
        </div>
        {canManage ? (
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto_auto] md:items-center">
            <Select value={selectedFileId} onValueChange={setSelectedFileId} disabled={isMutating}>
              <SelectTrigger aria-label="Select an existing canonical library file"><SelectValue placeholder="Select a library file" /></SelectTrigger>
              <SelectContent>{libraryFiles.map((file) => <SelectItem key={file.id} value={file.id}>{file.file_name}</SelectItem>)}</SelectContent>
            </Select>
            <Button type="button" variant="outline" disabled={!selectedFileId || isMutating} onClick={() => void createFromFile(selectedFileId)}>Add selected file</Button>
            <Button type="button" disabled={isMutating} onClick={() => fileRef.current?.click()} className="bg-[#1f3a30] text-white hover:bg-[#1f3a30]/90">
              {isUploading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Upload className="h-4 w-4" aria-hidden />} Upload file
            </Button>
            <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ""; if (file) void uploadAndCreate(file); }} />
            <label className="flex items-center gap-2 font-inter text-xs text-stone-600 md:col-span-3"><Checkbox checked={staffSourceVisible} onCheckedChange={(checked) => setStaffSourceVisible(checked === true)} /> Allow staff to open the source after it becomes active</label>
          </div>
        ) : <p className="rounded-lg bg-stone-200/70 px-3 py-2 font-inter text-xs text-stone-600">Only authorized school or district leaders can manage these documents.</p>}
      </header>

      <div className="pt-4 md:pt-5">
        {context.documents.isLoading ? <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-stone-500" aria-label="Loading organization context" /></div> : (
          <ul className="space-y-3">
            {documents.map((document) => <DocumentRow key={document.id} document={document} libraryFiles={libraryFiles} canManage={canManage} disabled={isMutating} onInclusion={changeInclusion} onSourceVisibility={changeSourceVisibility} onReplace={createReplacement} onDelete={setPendingDelete} />)}
            {!documents.length && <li className="rounded-xl border border-dashed border-stone-300 bg-white/60 px-4 py-6 text-center font-inter text-sm text-stone-500">No organization context documents yet.</li>}
          </ul>
        )}
      </div>

      <AlertDialog open={pendingDelete !== null} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Remove this document?</AlertDialogTitle><AlertDialogDescription>Chat access stops immediately. The canonical file and each context version are then queued for secure deletion.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => void removeDocument()} className="bg-red-600 text-white hover:bg-red-700">Remove</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};

function DocumentRow({ document, libraryFiles, canManage, disabled, onInclusion, onSourceVisibility, onReplace, onDelete }: {
  document: OrganizationContextDocument;
  libraryFiles: { id: string; file_name: string }[];
  canManage: boolean;
  disabled: boolean;
  onInclusion: (document: OrganizationContextDocument, requested: boolean) => Promise<void>;
  onSourceVisibility: (document: OrganizationContextDocument, checked: boolean) => Promise<void>;
  onReplace: (document: OrganizationContextDocument, fileId: string) => Promise<void>;
  onDelete: (document: OrganizationContextDocument) => void;
}) {
  const [replacementId, setReplacementId] = useState("");
  const latest = document.versions[0];
  const quarantine = document.versions.find((version) => version.lifecycleState === "quarantined");
  const pendingReplacement = document.versions.some((version) => ["queued", "extracting", "privacy_review", "indexing", "ready"].includes(version.lifecycleState));
  // HEY-211: the switch is never unlabeled — with the switch off, a neutral
  // gray "Use in Chat" shows next to it. When a genuine failure is live (see
  // HEY-213), the error state wins so leaders see the failed replacement and
  // act on it.
  // HEY-213: a failed newest revision is only a live error when it is a
  // replacement of a DIFFERENT canonical file than the one now active (
  // broken replacement). A same-file failed version is the inert leftover of
  // an earlier always-re-ingest attempt of the document's own content; after
  // a HEY-212 restore the restored version is active, so the row is green.
  const activeVersion = document.versions.find((version) => version.id === document.currentActiveVersionId) ?? null;
  const failedVersionIsReplacement = latest?.lifecycleState === "failed"
    && activeVersion !== null
    && latest.canonicalFileId !== activeVersion.canonicalFileId;
  const chatLabel = !document.requestedInclusion
    ? { text: "Use in Chat", className: "text-stone-500" }
    : failedVersionIsReplacement
      ? { text: "Used in Chat (ERROR)", className: "bg-red-100 text-red-800" }
      : document.effectiveInclusion
        ? { text: "Used in Chat", className: "bg-emerald-100 text-emerald-800" }
        : { text: "Used in Chat (pending)", className: "bg-stone-100 text-stone-600" };

  return <li className="rounded-xl border border-stone-200 bg-white p-4">
    {quarantine && <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden /><span><strong>Blocked from chat context.</strong> This revision is quarantined; the prior active version remains in use when available.</span></div>}
    <div className="flex flex-col gap-3 md:flex-row md:items-start">
      <FileText className="h-5 w-5 shrink-0 text-bodhi-teal" aria-hidden />
      <div className="min-w-0 flex-1"><p className="font-inter text-sm font-semibold text-stone-900">{document.title}</p><p className="mt-1 font-inter text-xs text-stone-500">{document.scope} context · Added {format(new Date(document.createdAt), "MMM d, yyyy")}</p><div className="mt-2 flex flex-wrap gap-2 font-inter text-xs"><span className="rounded-full bg-stone-100 px-2 py-1 text-stone-600">Latest revision: v{latest?.versionNumber ?? 0} {latest?.lifecycleState ?? "unknown"}</span></div></div>
      {canManage && <div className="flex flex-col items-end gap-2"><div className="flex items-center gap-2"><Switch checked={document.requestedInclusion} disabled={disabled} onCheckedChange={(checked) => void onInclusion(document, checked)} aria-label={`Toggle requested chat inclusion for ${document.title}`} /><Button type="button" variant="ghost" size="icon" disabled={disabled} onClick={() => onDelete(document)} aria-label={`Remove ${document.title}`}><Trash2 className="h-4 w-4 text-red-700" aria-hidden /></Button></div><span className={`rounded-full px-2 py-1 font-inter text-xs ${chatLabel.className}`}>{chatLabel.text}</span></div>}
    </div>
    <div className="mt-4 grid gap-3 border-t border-stone-100 pt-3 md:grid-cols-2">
      <label className="flex items-center gap-2 font-inter text-xs text-stone-600"><Checkbox checked={document.staffSourceVisible} disabled={!canManage || disabled} onCheckedChange={(checked) => void onSourceVisibility(document, checked === true)} /> Staff may open the source independently</label>
      {canManage && <div className="flex gap-2"><Select value={replacementId} onValueChange={setReplacementId} disabled={disabled || pendingReplacement}><SelectTrigger aria-label={`Choose replacement for ${document.title}`}><SelectValue placeholder="Choose replacement" /></SelectTrigger><SelectContent>{libraryFiles.filter((file) => file.id !== document.canonicalFileId).map((file) => <SelectItem key={file.id} value={file.id}>{file.file_name}</SelectItem>)}</SelectContent></Select><Button type="button" variant="outline" disabled={!replacementId || disabled || pendingReplacement} onClick={() => void onReplace(document, replacementId)}><Replace className="h-4 w-4" aria-hidden /> Replace</Button></div>}
    </div>
    <p className="mt-3 font-inter text-xs text-stone-500">Revisions: {document.versions.map((version) => `v${version.versionNumber} ${version.lifecycleState}`).join(" · ") || "none"}</p>
  </li>;
}
